import { CodeIssues, Image, JiraSoftwareLogoOutlined, Runtime } from "@/icons"

import type { ComponentType } from "react"
import type {
  TakenAction,
  TakenActionsByIssue,
  TakenActionType
} from "./TakenActions.types"

// One icon per action type — shared by the indicator strip and the action rows
// (same glyphs as the Remediation method cards).
export const ACTION_ICON: Record<
  TakenActionType,
  ComponentType<{ className?: string }>
> = {
  runtime: Runtime,
  pr: CodeIssues,
  ci: Image,
  jira: JiraSoftwareLogoOutlined
}

export const ACTION_TYPE_ORDER: TakenActionType[] = [
  "runtime",
  "pr",
  "ci",
  "jira"
]

export const ACTION_CELL_ORDER: TakenActionType[] = [
  "pr",
  "ci",
  "runtime",
  "jira"
]

export type TakenActionCounts = Record<TakenActionType, number>

export const JIRA_KEY_PREFIX = "KP-"
const JIRA_BASE_NUMBER = 652

// Each created Jira issue gets the next sequential key (KP-652, KP-653, ...).
// Jira keys are unique per project, so pass the actions of ALL issues — the
// sequence is global, never restarted per issue.
export const nextJiraReference = (actions: TakenAction[]): string => {
  const numbers = actions
    .filter((action) => action.type === "jira")
    .map((action) => Number(action.reference?.replace(JIRA_KEY_PREFIX, "")))
    .filter((value) => Number.isFinite(value))
  const next = numbers.length ? Math.max(...numbers) + 1 : JIRA_BASE_NUMBER
  return `${JIRA_KEY_PREFIX}${next}`
}

export const countActionsByType = (actions: TakenAction[]): TakenActionCounts =>
  actions.reduce<TakenActionCounts>(
    (counts, action) => ({ ...counts, [action.type]: counts[action.type] + 1 }),
    { runtime: 0, pr: 0, ci: 0, jira: 0 }
  )

// The name translation key (under triage.remediation.actions.types) per type.
export const ACTION_NAME_KEY: Record<TakenActionType, string> = {
  runtime: "runtimeMitigation",
  pr: "pullRequest",
  ci: "ciMitigationList",
  jira: "jiraIssue"
}

// Which affordances each row carries, per the Figma variants:
// pr = link, no status, no menu; jira = link, no status, menu;
// runtime/ci = plain reference, status badge, menu.
export const ACTION_ROW_META: Record<
  TakenActionType,
  { isLink: boolean; hasOverflow: boolean }
> = {
  runtime: { isLink: false, hasOverflow: true },
  pr: { isLink: true, hasOverflow: false },
  ci: { isLink: false, hasOverflow: true },
  jira: { isLink: true, hasOverflow: true }
}

export const selectActionCellIcons = (actions: TakenAction[], max: number) => {
  const counts = countActionsByType(actions)
  const types = ACTION_CELL_ORDER.filter((type) => counts[type] > 0)

  // Both halves count types: one icon per type, and +N for the types that did
  // not fit. Counting actions in N would mix the two granularities.
  return {
    shown: types.slice(0, max),
    hidden: types.slice(max).length
  }
}

export const EMPTY_ACTIONS: TakenAction[] = []

export const selectIssueActions = (
  byIssue: TakenActionsByIssue,
  issueId?: string
): TakenAction[] =>
  issueId ? (byIssue[issueId] ?? EMPTY_ACTIONS) : EMPTY_ACTIONS

export const selectProjectActions = (
  byIssue: TakenActionsByIssue,
  projectId?: string
): TakenAction[] => {
  if (!projectId) return EMPTY_ACTIONS

  return Object.values(byIssue)
    .flat()
    .filter((action) => action.projectId === projectId)
}

// The transitions below return the map unchanged when nothing moves, which is
// what lets setTakenActions skip the notify.
export const appendAction = (
  byIssue: TakenActionsByIssue,
  issueId: string,
  action: TakenAction
): TakenActionsByIssue => {
  const current = selectIssueActions(byIssue, issueId)

  if (current.some((item) => item.id === action.id)) return byIssue

  return { ...byIssue, [issueId]: [...current, action] }
}

export const appendJiraAction = (
  byIssue: TakenActionsByIssue,
  issueId: string,
  { projectId, createdAt }: { projectId?: string; createdAt: number }
): TakenActionsByIssue => {
  // Key numbering is global — Jira never repeats a key across issues.
  const reference = nextJiraReference(Object.values(byIssue).flat())

  return appendAction(byIssue, issueId, {
    id: `jira:${reference}`,
    type: "jira",
    projectId,
    reference,
    createdAt
  })
}

export const removeActionById = (
  byIssue: TakenActionsByIssue,
  issueId: string,
  actionId: string
): TakenActionsByIssue => {
  const remaining = selectIssueActions(byIssue, issueId).filter(
    (item) => item.id !== actionId
  )
  // Drop the key with its last action so the map stays clean.
  const { [issueId]: _, ...rest } = byIssue

  return remaining.length ? { ...rest, [issueId]: remaining } : rest
}
